import { Rijtijd, TvvStandaardRoute } from "@/entities/tvv";
import { RouteTitel } from "@/features/tvv/components/shared/index.ts";
import { Button, Checkbox, Icon } from "@/shared/components/ui";

type RouteSelectCheckboxItemProps = {
    type: "checkbox";
    route: TvvStandaardRoute;
    isSelected: boolean;
    onToggle: () => void;
};

export function RouteSelectCheckboxItem({ route, isSelected, onToggle }: Readonly<RouteSelectCheckboxItemProps>) {
    const rijtijdDisplay = Rijtijd.formatRijtijd(Rijtijd.getTotaleRijtijd(route.rijtijden));

    return (
        <li
            role="option"
            aria-selected={isSelected}
            className="flex cursor-pointer items-center gap-3 rounded-lg rounded-b-none border-b py-2"
            onClick={onToggle}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggle();
                }
            }}
            tabIndex={0}
            data-testid={`route-${route.stationCodes.join(">")}`}
        >
            <Checkbox checked={isSelected} />

            <div className="min-w-0 flex-1">
                <RouteTitel route={route} className="text-content-emphasis text-m" />
                <div className="text-content-subtle text-s">{TvvStandaardRoute.formatOrganisatie(route.organisatie)}</div>
            </div>

            <div className="shrink-0 text-right">
                <div className="text-content-subtle text-s">Rijtijd</div>
                <div className="text-s font-medium">{rijtijdDisplay}</div>
            </div>
        </li>
    );
}

type RouteSelectButtonItemProps = {
    type: "button";
    route: TvvStandaardRoute;
    onAdd: (route: TvvStandaardRoute) => void;
    isAdded: boolean;
};

export function RouteSelectButtonItem({ route, onAdd, isAdded }: Readonly<RouteSelectButtonItemProps>) {
    const rijtijdDisplay = Rijtijd.formatRijtijd(Rijtijd.getTotaleRijtijd(route.rijtijden));

    return (
        <li
            role="option"
            className="flex items-center gap-3 rounded-lg rounded-b-none border-b py-2"
            data-testid={`route-${route.stationCodes.join(">")}`}
        >
            <div className="min-w-0 flex-1">
                <RouteTitel route={route} className="text-content-emphasis text-m" />
                <div className="text-content-subtle text-s">{TvvStandaardRoute.formatOrganisatie(route.organisatie)}</div>
            </div>

            <div className="shrink-0 text-right">
                <div className="text-content-subtle text-s">Rijtijd</div>
                <div className="text-s font-medium">{rijtijdDisplay}</div>
            </div>

            <div className="flex basis-30 items-end justify-end">
                {!isAdded ? (
                    <Button type="button" variant="secondary" onClick={() => onAdd(route)}>
                        Toevoegen
                    </Button>
                ) : (
                    <Icon className="" name="check" />
                )}
            </div>
        </li>
    );
}

type BaseRouteSelectItemProps = {
    type: "button" | "checkbox";
};

type RouteSelectItemProps = BaseRouteSelectItemProps & (RouteSelectCheckboxItemProps | RouteSelectButtonItemProps);

export function RouteSelectItem(props: Readonly<RouteSelectItemProps>) {
    switch (props.type) {
        case "checkbox":
            return <RouteSelectCheckboxItem {...props} />;
        case "button":
            return <RouteSelectButtonItem {...props} />;
    }
}
